import React from 'react';
import * as consts from 'config/consts';
import NumberLabel from 'components/features/utils/NumberLabel';
import { useModal } from 'hooks/useModal';
import {
  ModalButtonInternal,
  getDataFetchingState,
} from 'components/features/home/PortfolioStuffing/asset/PortfolioStuffingAsset';
import { DataFetching } from 'components/features/home/PortfolioStuffing/asset/AssetOrganizedPortfolioItem';
import { formatNumber } from 'utils/formatter';
import Icon from 'components/commons/icon/Icon';
import { PositionItemsProps } from 'components/features/home/ProfitAndLossStuffing/ProfitAndLossStuffing';
import {
  isForeignStockTypes,
  isNotExistDetailTypes,
  ProductTypesLabel,
} from 'components/features/utils/productTypes';
import { isGoSymbolDetail } from 'components/features/utils/util';

const AboutExchangeRateModalButtonWithStateControl = ({
  isShowMessage,
  className,
  modalStates: modalStatesProps,
}: {
  isShowMessage: boolean;
  className?: string;
  modalStates: {
    isShowModal: boolean;
    hideModal: () => void;
    onClick: (() => void) | null;
  };
}) => {
  const modalStates = {
    isShowModal: modalStatesProps.isShowModal,
    hideModal: modalStatesProps.hideModal,
    onClick: modalStatesProps.onClick ?? undefined,
  };
  return (
    <>
      <ModalButtonInternal
        {...{
          isShowMessage,
          className,
          modalStates,
        }}
      />
    </>
  );
};

type PositionItemProps = {
  borderTop: React.ReactElement | false;
  position: PositionItemsProps;
  onTradeItemClick: (symbol: string) => void;
};

export const PositionItem = ({
  borderTop,
  position,
  onTradeItemClick: onTradeItemClick_,
}: PositionItemProps): React.ReactElement => {
  const [isShowModal, { hideModal, showModal }] = useModal();
  const isNotExistDetail = isNotExistDetailTypes(position.productType);
  const isForeignStock = isForeignStockTypes(position.productType);

  type DataFetchingState = 'Fetching' | 'Fetched';
  const dataFetchingState: DataFetchingState = getDataFetchingState(
    position.symbol,
    position.productType === consts.productTypes.投資信託
      ? consts.AssetType['投資信託']
      : consts.AssetType['その他']
  );

  const onTradeItemClick = (symbol: string, productType: number) => {
    if (
      dataFetchingState === 'Fetching' ||
      !isGoSymbolDetail(symbol, productType)
    )
      return;
    onTradeItemClick_(symbol);
  };

  const getDataFetchingTextStyle = (defaultTextStyle: string): string =>
    dataFetchingState === 'Fetching' ? 'text-bluegray-300' : defaultTextStyle;

  const isStockMargin = position.cashMargin === consts.CashMargin.信用;

  return (
    <>
      {borderTop}
      <div
        className={`flex items-center mx-3 py-2 ${
          !isForeignStock ? 'cursor-pointer' : ''
        }`}>
        <div
          className="w-full"
          onClick={(): void =>
            isNotExistDetail
              ? showModal()
              : onTradeItemClick(position.symbol, position.productType)
          }>
          {dataFetchingState === 'Fetching' && (
            <div className="flex flex-row mb-1">
              <DataFetching />
            </div>
          )}
          <div>
            {position.symbol && (
              <span className="text-xs text-bluegray-500 mr-3">
                {position.symbol}
              </span>
            )}
            <span
              className={`text-sm font-bold ${getDataFetchingTextStyle('')}`}>
              {position.symbolName}
            </span>
          </div>
          <div className="flex mt-1">
            <ProductTypesLabel
              productType={position.productType}
              cashMargin={position.cashMargin}
            />
            <div className="flex-grow">
              <div className="flex">
                <div className="text-xs text-bluegray-500 flex-grow flex justify-end">
                  前日比
                </div>
                <div className="flex-grow flex justify-end mr-3 max-w-28 min-w-28">
                  <NumberLabel
                    value={position.diffProfitOrLoss}
                    unitName="円"
                    classNames={[
                      'text-base ' + getDataFetchingTextStyle(''),
                      'text-xs mt-1 ml-1 ' + getDataFetchingTextStyle(''),
                    ]}
                    isDispalyNoValueUnit
                  />
                </div>
              </div>
              <div className="flex">
                <div className="text-xs text-bluegray-500 flex-grow flex justify-end">
                  {isStockMargin ? '評価損益' : '評価額'}
                </div>
                <div
                  className={`text-sm flex-grow flex justify-end mr-3 max-w-28 min-w-28 ${getDataFetchingTextStyle(
                    ''
                  )}`}>
                  {formatNumber(position.validationAmount, {
                    unitName: '',
                    noValueText: '---',
                  })}
                  <div className="text-xs mt-1 ml-1">円</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {isForeignStock ? (
          <AboutExchangeRateModalButtonWithStateControl
            isShowMessage={false}
            className="text-base"
            modalStates={{
              isShowModal,
              hideModal,
              onClick: showModal,
            }}
          />
        ) : (
          <div
            onClick={(): void =>
              onTradeItemClick(position.symbol, position.productType)
            }>
            {dataFetchingState === 'Fetching' ||
            !isGoSymbolDetail(position.symbol, position.productType) ? (
              <Icon
                name="chevron-right"
                className="text-lg w-4 text-transparent"
              />
            ) : (
              <Icon
                name="chevron-right"
                className="text-lg w-4 text-bluegray-500"
              />
            )}
          </div>
        )}
      </div>
    </>
  );
};
